import { z } from 'zod';
import { MemberDTO, MemberStatus } from './members.js';
import { MatchDTO, MatchStatus } from './matches.js';
import { RelationshipDTO, RelationshipType } from './network.js';

// ===========================================================================
// Export — die ausgehende Form. Nutzt die normalen DTOs, damit ein Backup
// exakt das enthält, was die App auch sonst ausliefert.
// ===========================================================================

/** `geocodedAt` steht in keinem normalen DTO, gehört aber zum Datenbestand. */
export const BackupMemberDTO = MemberDTO.extend({
  geocodedAt: z.string().nullable(),
});
export type BackupMemberDTO = z.infer<typeof BackupMemberDTO>;

export const BackupData = z.object({
  members: z.array(BackupMemberDTO),
  matches: z.array(MatchDTO),
  relationships: z.array(RelationshipDTO),
});
export type BackupData = z.infer<typeof BackupData>;

/**
 * 3 = nur noch Mitglieder, Matches, Verbindungen.
 * 2 = mit Relationships, Events, Deal-Beteiligten. 1 = alles davor.
 *
 * Ältere Dateien bleiben lesbar: zod verwirft beim Import die Felder
 * abgeschalteter Module still, statt die Datei abzulehnen.
 */
export const BACKUP_VERSION = 3;

export const BackupExport = z.object({
  version: z.number().int(),
  exportedAt: z.string(),
  user: z.object({
    email: z.string(),
    name: z.string().nullable(),
    role: z.string(),
  }),
  data: BackupData,
});
export type BackupExport = z.infer<typeof BackupExport>;

// ===========================================================================
// Import — bewusst NICHT die Export-DTOs. Ein Backup, das vor einem
// Schema-Ausbau erstellt wurde, kennt neuere Pflichtfelder nicht; würde der
// Import gegen die strengen DTOs prüfen, wäre eine ältere Datei nicht mehr
// einlesbar. Darum: alles Neue optional mit sinnvollem Default.
// ===========================================================================

const nullStr = z.string().nullish().default(null);
const nullNum = z.number().nullish().default(null);
const strArr = z.array(z.string()).nullish().default([]).transform((v) => v ?? []);

export const BackupImportMember = z.object({
  id: z.string(),
  name: z.string().min(1),
  firstName: nullStr,
  lastName: nullStr,
  company: nullStr,
  role: nullStr,
  industry: nullStr,
  email: nullStr,
  phone: nullStr,
  website: nullStr,
  linkedin: nullStr,
  instagram: nullStr,
  xing: nullStr,
  street: nullStr,
  zip: nullStr,
  city: nullStr,
  country: nullStr,
  lat: nullNum,
  lng: nullNum,
  geocodedAt: nullStr,
  birthday: nullStr,
  tags: strArr,
  notes: nullStr,
  status: MemberStatus.nullish().default('ACTIVE').transform((v) => v ?? 'ACTIVE'),
  photoUrl: nullStr,
  isSelf: z.boolean().nullish().default(false).transform((v) => v ?? false),
  pitch: nullStr,
  offering: nullStr,
  seeking: nullStr,
});
export type BackupImportMember = z.infer<typeof BackupImportMember>;

// Nur die IDs interessieren — die vollen Member-Objekte im Backup werden verworfen.
const memberRef = z.object({ id: z.string() });

export const BackupImportMatch = z.object({
  id: z.string().nullish().default(null),
  memberA: memberRef,
  memberB: memberRef,
  score: z.number().int().nullish().default(0).transform((v) => v ?? 0),
  reasons: strArr,
  status: MatchStatus.nullish().default('SUGGESTED').transform((v) => v ?? 'SUGGESTED'),
  notes: nullStr,
});
export type BackupImportMatch = z.infer<typeof BackupImportMatch>;

export const BackupImportRelationship = z.object({
  aId: z.string(),
  bId: z.string(),
  type: RelationshipType.nullish().default('OTHER').transform((v) => v ?? 'OTHER'),
  strength: z.number().int().nullish().default(50).transform((v) => v ?? 50),
  hidden: z.boolean().nullish().default(false).transform((v) => v ?? false),
  note: nullStr,
});
export type BackupImportRelationship = z.infer<typeof BackupImportRelationship>;

function list<T extends z.ZodTypeAny>(item: T) {
  return z
    .array(item)
    .nullish()
    .default([])
    .transform((v) => v ?? []);
}

export const BackupImportData = z.object({
  members: list(BackupImportMember),
  matches: list(BackupImportMatch),
  relationships: list(BackupImportRelationship),
});
export type BackupImportData = z.infer<typeof BackupImportData>;

export const BackupImportInput = z.object({
  mode: z.enum(['merge', 'replace']),
  /** Fehlt bei Backups vor Version 2. */
  version: z.number().int().nullish().default(null),
  data: BackupImportData,
});
export type BackupImportInput = z.infer<typeof BackupImportInput>;

export const BackupImportResult = z.object({
  createdCounts: z.object({
    members: z.number(),
    matches: z.number(),
    relationships: z.number(),
  }),
  errors: z.array(z.string()),
});
export type BackupImportResult = z.infer<typeof BackupImportResult>;
